import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { FieldType as AdvancedFilterFieldType } from "@via/components/advanced-filter/helpers/types";
import { getFieldTypeFromString } from "@via/components/advanced-filter/helpers/utils";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import {
  ExplorerRequestParams,
  FileQueryFilter,
  FilterLogic,
  MetadataFilter,
  PipelineFilter,
  type SampleExplorerRunExportMode,
} from "@via/pages/sample-trackers/samples/types/filters.ts";
import {
  convertFiltersToDataFilters,
  convertFiltersToRunFilters,
  convertFileQueryFiltersToOutputFilters,
} from "@via/pages/sample-trackers/samples/utils/filter-helpers.ts";
import {
  type CreateSampleExplorerViewRequestBody,
  type SampleExplorerView,
  type SampleExplorerViewResponseBody,
  type SampleExplorerViewsResponseBody,
  RunStatus,
  type VmetaExplorerVisibleColumn,
  type UpdateSampleExplorerViewRequestBody,
} from "common";
import { AxiosResponse } from "axios";
import {
  getAxiosErrorMessage,
  getBlobErrorMessage,
} from "@via/utils/helpers.tsx";

export interface Permission {
  type: "user" | "group";
  userId?: string;
  groupId?: string;
  read: boolean;
  write: boolean;
  email?: string;
  username?: string;
  name?: string;
  groupName?: string;
}

export interface RunData {
  runId: number;
  runUUID: string;
  pipelineId: number;
  runName: string;
  pipelineName: string;
  pipelineVersion: string;
  /** Undefined is treated as "pipeline", matching RunPipelineLink and every row created
   *  before biocorepipe_save carried subject_type. */
  subjectType?: "pipeline" | "process";
  runStatus: RunStatus;
  reports?: {
    [reportRelativePath: string]: {
      [fieldName: string]: number | null;
    };
  };
}

export interface RunSummary {
  runCount: number;
  runIds: string;
  pipelineName: string;
  runNames: string;
  pipelineVersion: string;
}

export interface RunReports {
  [reportRelativePath: string]: {
    [fieldName: string]: number | null;
  };
}

export interface SampleData {
  _id: string;
  canvasId?: string;
  canvas_label?: string;
  perms?: Permission[];
  id: string;
  name: string;
  s3_archive_dir: string;
  gs_archive_dir: string;
  file_layout?: string;
  string_field: string;
  number_field: number | null;
  boolean_field: boolean;
  date_field?: string;
  updatedAt: string;
  file1: string;
  file2: string;
  file3: string;
  file4: string;
  geo_id: string;
  runData: RunData[];
  runSummary: RunSummary;
  runReports: RunReports;
}

export interface FieldType {
  type: string;
  name: string;
  label: string;
  hasReference: boolean;
  referencedIdField: string | null;
  isIdentifier: boolean;
  hasNamingPattern: boolean;
}

export interface CanvasFieldsResponse {
  status: string;
  data: FieldType[];
}

export interface SamplesResponse {
  status: string;
  results: number;
  data: SampleData[];
  meta: {
    field_types: FieldType[];
    runReportFields?: Record<string, { name: string; type: "Number" }[]>;
  };
}

export interface ReportPathsResponse {
  status: string;
  data: string[];
}

export interface ReportFeaturesResponse {
  status: string;
  data: string[];
}

const sampleExplorerViewsQueryKey = (canvasId: string): string[] => [
  "/api/v1/vmeta/canvas/sample-explorer/view",
  canvasId,
];

type SampleExplorerRequestParams = {
  canvasIds: string[];
  format?: string;
  visibleColumns?: VmetaExplorerVisibleColumn[];
  exportRunMode?: SampleExplorerRunExportMode;
  metadataFilters?: MetadataFilter[];
  metadataLogic?: FilterLogic;
  pipelineFilters?: PipelineFilter[];
  pipelineLogic?: FilterLogic;
  fileQueryFilters?: FileQueryFilter[];
  fileQueryLogic?: FilterLogic;
  take?: number;
  skip?: number;
  sort?: {
    category: "canvasFields" | "runReports";
    column: string;
    reportRelativePath?: string;
  };
  order?: "asc" | "desc";
};

type ExportSampleExplorerRequest = SampleExplorerRequestParams & {
  fileName?: string;
};

const buildExplorerRequestParams = (
  params?: SampleExplorerRequestParams,
  options?: { includePagination?: boolean }
): ExplorerRequestParams => {
  const requestParams: ExplorerRequestParams = {
    canvasIds: params?.canvasIds ?? [],
  };
  const includePagination = options?.includePagination ?? true;

  if (includePagination) {
    requestParams.take = params?.take ?? 20;
    requestParams.skip = params?.skip ?? 0;
  }

  if (params?.format) requestParams.format = params.format;
  if (params?.visibleColumns && params.visibleColumns.length > 0) {
    requestParams.visibleColumns = params.visibleColumns;
  }
  if (params?.exportRunMode) requestParams.exportRunMode = params.exportRunMode;
  if (params?.sort) requestParams.sort = params.sort;
  if (params?.order) requestParams.order = params.order;

  if (params?.metadataFilters && params.metadataFilters.length > 0) {
    const dataFilters = convertFiltersToDataFilters(
      params.metadataFilters,
      params.metadataLogic || "OR"
    );
    if (dataFilters) requestParams.dataFilters = dataFilters;
  }

  if (params?.pipelineFilters && params.pipelineFilters.length > 0) {
    const runFilters = convertFiltersToRunFilters(
      params.pipelineFilters,
      params.pipelineLogic || "OR"
    );
    if (runFilters) requestParams.runFilters = runFilters;
  }

  if (params?.fileQueryFilters && params.fileQueryFilters.length > 0) {
    const outputFilters = convertFileQueryFiltersToOutputFilters(
      params.fileQueryFilters,
      params.fileQueryLogic || "OR"
    );
    if (outputFilters) requestParams.outputFilters = outputFilters;
  }

  return requestParams;
};

export const fetchCanvasFields = async (
  canvasId: string
): Promise<CanvasFieldsResponse> => {
  const res = await axios.get(
    `/api/v1/vmeta/canvas/${canvasId}/data/file/fields?mode=insert`
  );
  return res.data;
};

export const useCanvasFields = (
  canvasId: string
): UseQueryResult<AdvancedFilterFieldType[], Error> => {
  return useQuery<CanvasFieldsResponse, Error, AdvancedFilterFieldType[]>({
    queryKey: ["/api/v1/vmeta/canvas/fields", canvasId],
    queryFn: async () => {
      if (!canvasId) throw new Error("Canvas ID is required");
      return await fetchCanvasFields(canvasId);
    },
    enabled: !!canvasId,
    retry: false,
    select: data => {
      // Transform API response to AdvancedFilterFieldType format
      const transformedFields = data.data.map(field => ({
        name: field.name,
        label: field.label,
        type: getFieldTypeFromString(field.type),
        hasNamingPattern: field.hasNamingPattern,
      }));
      return transformedFields;
    },
  });
};

// Fetch report paths
export const fetchReportPaths = async (
  canvasId: string
): Promise<ReportPathsResponse> => {
  const res = await axios.get(
    `/api/v1/vmeta/canvas/${canvasId}/data/file/explorer/reportPaths`
  );
  return res.data;
};

export const useReportPaths = (
  canvasId: string
): UseQueryResult<string[], Error> => {
  return useQuery<ReportPathsResponse, Error, string[]>({
    queryKey: ["/api/v1/vmeta/canvas/reportPaths", canvasId],
    queryFn: async () => {
      if (!canvasId) throw new Error("Canvas ID is required");
      return await fetchReportPaths(canvasId);
    },
    enabled: !!canvasId,
    retry: false,
    select: data => {
      return data.data;
    },
  });
};

// Fetch report features
export const fetchReportFeatures = async (
  canvasId: string,
  relativeReportPath: string
): Promise<ReportFeaturesResponse> => {
  const res = await axios.post(
    `/api/v1/vmeta/canvas/${canvasId}/data/file/explorer/reportFeatures`,
    { relativeReportPath }
  );
  return res.data;
};

export const useReportFeatures = (
  canvasId: string,
  relativeReportPath: string | null
): UseQueryResult<string[], Error> => {
  return useQuery<ReportFeaturesResponse, Error, string[]>({
    queryKey: [
      "/api/v1/vmeta/canvas/reportFeatures",
      canvasId,
      relativeReportPath,
    ],
    queryFn: async () => {
      if (!canvasId || !relativeReportPath)
        throw new Error("Canvas ID and report path are required");
      return await fetchReportFeatures(canvasId, relativeReportPath);
    },
    enabled: !!canvasId && !!relativeReportPath,
    retry: false,
    select: data => {
      return data.data;
    },
  });
};

// Fetch sample explorer data with filters
const fetchSampleExplorer = async (
  params?: SampleExplorerRequestParams
): Promise<SamplesResponse> => {
  const requestParams = buildExplorerRequestParams(params);
  const url = `/api/v1/vmeta/canvas/search/populated/explorer`;

  try {
    const res = await axios.post(url, requestParams);
    return res.data;
  } catch (e: unknown) {
    throw new Error(getAxiosErrorMessage(e) ?? "Failed to fetch samples.");
  }
};

export const useExportSampleExplorer = (): UseMutationResult<
  AxiosResponse<Blob>,
  Error,
  ExportSampleExplorerRequest
> => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<AxiosResponse<Blob>, Error, ExportSampleExplorerRequest>({
    mutationFn: async ({ fileName, ...params }) => {
      if (!Array.isArray(params.canvasIds) || params.canvasIds.length === 0) {
        throw new Error("At least one Canvas ID is required");
      }
      const requestParams = buildExplorerRequestParams(params, {
        includePagination: false,
      });

      try {
        return await axios.post(
          "/api/v1/vmeta/canvas/search/populated/explorer/download",
          requestParams,
          {
            responseType: "blob",
            withCredentials: true,
          }
        );
      } catch (error) {
        throw new Error(
          (await getBlobErrorMessage(error)) ??
            "We couldn't export the sample explorer data."
        );
      }
    },
    onSuccess: (response, variables) => {
      try {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = variables.fileName || "sample-explorer.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        setNotification({
          id: "sample-explorer-export-success",
          message: "Export started",
          details: "Sample explorer data is downloading.",
          variant: NOTIFICATION.SUCCESS,
        });
      } catch (error) {
        console.error("Error downloading file:", error);
        setNotification({
          id: "sample-explorer-export-error",
          message: "Download failed",
          details: "Error occurred while downloading the export file.",
          variant: NOTIFICATION.ERROR,
        });
      }
    },
    onError: error => {
      setNotification({
        id: "sample-explorer-export-failed",
        message: "Export failed",
        details:
          error.message || "We couldn't export the sample explorer data.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useSampleExplorer = (
  params: SampleExplorerRequestParams
): UseQueryResult<SamplesResponse, Error> => {
  return useQuery<SamplesResponse, Error>({
    queryKey: ["/api/v1/vmeta/canvas/explorer", params],
    queryFn: async () => {
      if (!params.canvasIds || params.canvasIds.length === 0)
        throw new Error("At least one Canvas ID is required");
      return await fetchSampleExplorer(params);
    },
    enabled: Array.isArray(params.canvasIds) && params.canvasIds.length > 0,
    retry: false,
  });
};

const fetchSampleExplorerViews = async (
  canvasId: string
): Promise<SampleExplorerViewsResponseBody> => {
  try {
    const res = await axios.get(
      `/api/v1/vmeta/canvas/${canvasId}/sample-explorer/view`
    );
    return res.data;
  } catch (e: unknown) {
    throw new Error(
      getAxiosErrorMessage(e) ?? "Failed to load saved sample explorer views."
    );
  }
};

const createSampleExplorerView = async (
  canvasId: string,
  body: CreateSampleExplorerViewRequestBody
): Promise<SampleExplorerViewResponseBody> => {
  try {
    const res = await axios.post(
      `/api/v1/vmeta/canvas/${canvasId}/sample-explorer/view`,
      body
    );
    return res.data;
  } catch (e: unknown) {
    throw new Error(
      getAxiosErrorMessage(e) ?? "Failed to save the sample explorer view."
    );
  }
};

const updateSampleExplorerView = async (
  canvasId: string,
  viewId: number,
  body: UpdateSampleExplorerViewRequestBody
): Promise<SampleExplorerViewResponseBody> => {
  try {
    const res = await axios.patch(
      `/api/v1/vmeta/canvas/${canvasId}/sample-explorer/view/${viewId}`,
      body
    );
    return res.data;
  } catch (e: unknown) {
    throw new Error(
      getAxiosErrorMessage(e) ?? "Failed to update the sample explorer view."
    );
  }
};

const deleteSampleExplorerView = async (
  canvasId: string,
  viewId: number
): Promise<void> => {
  try {
    await axios.delete(
      `/api/v1/vmeta/canvas/${canvasId}/sample-explorer/view/${viewId}`
    );
  } catch (e: unknown) {
    throw new Error(
      getAxiosErrorMessage(e) ?? "Failed to delete the sample explorer view."
    );
  }
};

export const useSampleExplorerViews = (
  canvasId: string
): UseQueryResult<SampleExplorerView[], Error> => {
  return useQuery<SampleExplorerViewsResponseBody, Error, SampleExplorerView[]>(
    {
      queryKey: sampleExplorerViewsQueryKey(canvasId),
      queryFn: async () => {
        if (!canvasId) throw new Error("Canvas ID is required");
        return await fetchSampleExplorerViews(canvasId);
      },
      enabled: !!canvasId,
      retry: false,
      select: data => data.data,
    }
  );
};

export const useCreateSampleExplorerView = (
  canvasId: string
): UseMutationResult<
  SampleExplorerView,
  Error,
  CreateSampleExplorerViewRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    SampleExplorerView,
    Error,
    CreateSampleExplorerViewRequestBody
  >({
    mutationFn: async body => {
      if (!canvasId) throw new Error("Canvas ID is required");
      const response = await createSampleExplorerView(canvasId, body);
      return response.data;
    },
    onSuccess: async view => {
      await queryClient.invalidateQueries({
        queryKey: sampleExplorerViewsQueryKey(canvasId),
      });
      setNotification({
        id: "sample-explorer-view-created",
        message: "Saved view created",
        details: `"${view.label}" is now available in the saved views list.`,
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: error => {
      setNotification({
        id: "sample-explorer-view-create-failed",
        message: "Unable to save view",
        details: error.message || "We couldn't save the sample explorer view.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateSampleExplorerView = (
  canvasId: string
): UseMutationResult<
  SampleExplorerView,
  Error,
  { viewId: number; body: UpdateSampleExplorerViewRequestBody }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    SampleExplorerView,
    Error,
    { viewId: number; body: UpdateSampleExplorerViewRequestBody }
  >({
    mutationFn: async ({ viewId, body }) => {
      if (!canvasId) throw new Error("Canvas ID is required");
      const response = await updateSampleExplorerView(canvasId, viewId, body);
      return response.data;
    },
    onSuccess: async view => {
      await queryClient.invalidateQueries({
        queryKey: sampleExplorerViewsQueryKey(canvasId),
      });
      setNotification({
        id: "sample-explorer-view-updated",
        message: "Saved view updated",
        details: `"${view.label}" has been updated.`,
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: error => {
      setNotification({
        id: "sample-explorer-view-update-failed",
        message: "Unable to update view",
        details:
          error.message || "We couldn't update the sample explorer view.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteSampleExplorerView = (
  canvasId: string
): UseMutationResult<void, Error, number> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<void, Error, number>({
    mutationFn: async viewId => {
      if (!canvasId) throw new Error("Canvas ID is required");
      await deleteSampleExplorerView(canvasId, viewId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: sampleExplorerViewsQueryKey(canvasId),
      });
      setNotification({
        id: "sample-explorer-view-deleted",
        message: "Saved view deleted",
        details: "The sample explorer view has been removed.",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: error => {
      setNotification({
        id: "sample-explorer-view-delete-failed",
        message: "Unable to delete view",
        details:
          error.message || "We couldn't delete the sample explorer view.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
